import { useMemo, useState } from 'react';
import type { ReactElement } from 'react';
import { ChevronDown, Plus } from 'lucide-react';

import { usePatchStream, useSetPositions } from '../api/planning-hooks';
import type { Person, Project, RegistryDocument, Stream } from '../api/types';
import { ProjectEditModal } from '../components/ProjectEditModal';
import { ProjectTable } from '../components/ProjectTable';
import { FilterBar } from '../components/FilterBar';
import { RegistryGate } from '../components/RegistryGate';
import { SummaryChips } from '../components/SummaryChips';
import { CreateButton, FilteredEmptyState, PageHeader } from '../components/view-parts';
import { useManualOrder } from '../hooks/useManualOrder';
import { SORT_LABELS, matchesFilters, sortProjects, statusRankLookup } from '../lib/filters';
import type { ProjectSortKey } from '../lib/filters';
import { orderedIds, sortProjectsManual } from '../lib/manual-order';
import { useFilters, useRegistryFilterContext } from '../state/FiltersProvider';

/**
 * F-25 — the streams view has one sort mode MORE than the other views:
 * «Вручную», ordered by `project.position` (missing positions last, in array
 * order). Only here is the drag handle live, like in the old static app.
 */
type StreamSortKey = ProjectSortKey | 'manual';

const STREAM_SORT_LABELS: Record<StreamSortKey, string> = { ...SORT_LABELS, manual: 'Вручную' };
const STREAM_SORT_KEYS = Object.keys(STREAM_SORT_LABELS) as StreamSortKey[];

/**
 * F-09: «По потокам» — projects grouped by stream, default order is queue №1
 * (stream priority: rank → due date → title), plus per-stream re-sorting.
 * Sections collapse; the count badge counts the FILTERED projects of the stream.
 */
export function StreamsPage(): ReactElement {
  return <RegistryGate label="Загрузка реестра">{(registry) => <StreamsView registry={registry} />}</RegistryGate>;
}

function StreamsView({ registry }: { registry: RegistryDocument }): ReactElement {
  const { filters, isActive } = useFilters();
  const { maps } = useRegistryFilterContext();
  const [collapsed, setCollapsed] = useState<ReadonlySet<string>>(new Set());
  const [sorts, setSorts] = useState<Record<string, StreamSortKey>>({});
  // Edit by ID (see ProjectsPage): a healed cache after a 409 re-opens the
  // dialog on the fresh project, never on a captured stale row.
  const [editingId, setEditingId] = useState<string | null | undefined>(undefined);
  // The per-stream «+» creates straight INTO its stream: a sentinel separate
  // from `editingId` (null id = the plain page-level create) that is passed to
  // the card as `defaultStreamId` — the new project cannot land anywhere else.
  const [createStreamId, setCreateStreamId] = useState<string | undefined>(undefined);
  // Per-stream status VISIBILITY (owner request 2026-10-15): local state only,
  // same shape as the per-stream sort selects and the per-card status filter on
  // «Исполнители». Default: every status is visible EXCEPT «Сделано» — the
  // stream's own pill row exposes the others; clicking a hidden pill (e.g. the
  // «Сделано» pill) adds it back, clicking a visible pill hides it. Sections
  // are independent: hiding «Сделано» in stream A leaves stream B alone.
  // Per-stream state, deliberately not URL-synced (a key per stream would be
  // unusable; mirrors the per-stream sort precedent).
  // `explicitHidden` records USER toggles; the default-hide of «Сделано» is
  // applied UNLESS the user has ever clicked the «Сделано» pill for that
  // section (tracked in `doneOverridden`). The pill click on «Сделано»
  // therefore has a real "first click shows it, second click hides it" feel.
  const [explicitHidden, setExplicitHidden] = useState<Record<string, ReadonlySet<string>>>({});
  const [doneOverridden, setDoneOverridden] = useState<Record<string, boolean>>({});
  const isHidden = (streamId: string, statusId: string): boolean => {
    if (explicitHidden[streamId]?.has(statusId)) return true;
    if (statusId === 'done' && !doneOverridden[streamId]) return true;
    return false;
  };
  const toggleStatus = (streamId: string, statusId: string) => {
    if (statusId === 'done') {
      // Latch the override the first time the user touches the «Сделано» pill.
      setDoneOverridden((current) => (current[streamId] ? current : { ...current, [streamId]: true }));
    }
    setExplicitHidden((current) => {
      const currentSet = current[streamId] ?? new Set<string>();
      const next = new Set(currentSet);
      if (next.has(statusId)) next.delete(statusId);
      else next.add(statusId);
      return { ...current, [streamId]: next };
    });
  };
  const editing =
    editingId === undefined
      ? undefined
      : editingId === null
        ? null
        : registry.projects.find((project) => project.id === editingId);

  const filteredByStream = useMemo(() => {
    const result = new Map<string, Project[]>();
    if (!maps) return result;
    for (const project of registry.projects) {
      if (!matchesFilters(project, filters, maps)) continue;
      const list = result.get(project.streamId) ?? [];
      list.push(project);
      result.set(project.streamId, list);
    }
    return result;
  }, [registry.projects, filters, maps]);

  const toggle = (streamId: string) => {
    setCollapsed((current) => {
      const next = new Set(current);
      if (next.has(streamId)) next.delete(streamId);
      else next.add(streamId);
      return next;
    });
  };

  // A project pointing at a stream id that is no longer in the dictionary
  // still has to be visible somewhere — a "no stream" section at the end.
  const orphans = useMemo(() => {
    const known = new Set(registry.streams.map((stream) => stream.id));
    const list: Project[] = [];
    for (const [streamId, projects] of filteredByStream) {
      if (!known.has(streamId)) list.push(...projects);
    }
    return list;
  }, [filteredByStream, registry.streams]);

  const visibleStreams = registry.streams.filter(
    (stream) => !(filters.streamId && !filters.streamId.includes(stream.id)),
  );

  // Owner feedback #2: an ACTIVE filter that empties a section hides the whole
  // section (no header, no «Фильтры исключили все проекты» card) — real
  // emptiness without filters stays visible. The page-level card below is the
  // LAST resort: it appears only when every section is gone, so the page is
  // never a silent blank.
  const everythingFilteredOut =
    maps !== null &&
    orphans.length === 0 &&
    visibleStreams.every((stream) => (filteredByStream.get(stream.id) ?? []).length === 0);

  return (
    <>
      <PageHeader
        title="По потокам"
        sub="Очередь №1 — приоритет потока; сортировку внутри потока можно поменять (в «Вручную» — перетаскиванием)."
        actions={<CreateButton label="Новый проект" onClick={() => setEditingId(null)} />}
      />

      <SummaryChips />

      <FilterBar />

      {visibleStreams.map((stream) => {
        const projects = filteredByStream.get(stream.id) ?? [];
        // Apply the per-stream status visibility AFTER the global filters
        // (matches the per-card status filter on «Исполнители»). The set
        // is derived from `isHidden` (default-hide of «Сделано» + the
        // user's explicit toggles); sections are independent.
        const visibleProjects = projects.filter(
          (project) => !isHidden(stream.id, project.status),
        );
        if (isActive && visibleProjects.length === 0) return null;
        return (
          <StreamSection
            key={stream.id}
            stream={stream}
            projects={projects}
            visibleProjects={visibleProjects}
            statuses={registry.statuses}
            isHidden={(statusId) => isHidden(stream.id, statusId)}
            onToggleStatus={(statusId) => toggleStatus(stream.id, statusId)}
            registry={registry}
            collapsed={collapsed.has(stream.id)}
            onToggle={() => toggle(stream.id)}
            sort={sorts[stream.id] ?? 'manual'}
            onSortChange={(sort) => setSorts((current) => ({ ...current, [stream.id]: sort }))}
            onEdit={(project) => setEditingId(project.id)}
            onAddProject={() => setCreateStreamId(stream.id)}
          />
        );
      })}

      {orphans.length > 0 ? (
        <section className="card card--flush stream-section" aria-label="Проекты без потока">
          <header className="stream-section__header">
            <span className="stream-section__name">Без потока</span>
            <span className="badge stream-section__count">{orphans.length}</span>
          </header>
          {/* No manual order here: `position` belongs to a stream, and this
              section has none — the shared sorts apply, no drag column. */}
          <ProjectTable
            projects={sortProjects(
              orphans,
              (sorts.__none__ as ProjectSortKey | undefined) ?? 'priority',
              undefined,
              undefined,
              undefined,
              statusRankLookup(registry.statuses),
            )}
            registry={registry}
            showStream
            onEdit={(project) => setEditingId(project.id)}
            emptyState={<FilteredEmptyState />}
          />
        </section>
      ) : null}

      {everythingFilteredOut ? (
        <section className="card">
          <FilteredEmptyState />
        </section>
      ) : null}

      {/* Editing resolves the project by id (409-healed cache); creating is
          `null` — optionally pre-bound to the «+» stream via defaultStreamId. */}
      {editing !== undefined || createStreamId !== undefined ? (
        <ProjectEditModal
          open
          onClose={() => {
            setEditingId(undefined);
            setCreateStreamId(undefined);
          }}
          registry={registry}
          project={editing ?? null}
          defaultStreamId={createStreamId}
        />
      ) : null}
    </>
  );
}

interface StreamSectionProps {
  stream: RegistryDocument['streams'][number];
  /**
   * The global-filtered projects of the stream (what the section counts and
   * what the per-status pills count — counting AFTER the global filter and
   * BEFORE the per-stream status filter is the honest set).
   */
  projects: Project[];
  /**
   * The post-everything visible projects (global filter + per-stream status
   * filter). What the table actually renders.
   */
  visibleProjects: Project[];
  /** The managed status dictionary, in display order. */
  statuses: RegistryDocument['statuses'];
  /** True when the section's table should currently hide this status. */
  isHidden: (statusId: string) => boolean;
  onToggleStatus: (statusId: string) => void;
  registry: RegistryDocument;
  collapsed: boolean;
  onToggle: () => void;
  sort: StreamSortKey;
  onSortChange: (sort: StreamSortKey) => void;
  onEdit: (project: Project) => void;
  /** The «+»: opens the create card pre-bound to THIS stream (owner request). */
  onAddProject: () => void;
}

/**
 * One stream section: header with the per-stream sort + a collapsible table.
 * Its drag state lives here (`useManualOrder`) so the page does not carry one
 * shared drag machine — only one drag can be in flight in a browser anyway,
 * and a drag from another section simply has `dragId === null` here.
 */
function StreamSection({
  stream,
  projects,
  visibleProjects,
  statuses,
  isHidden,
  onToggleStatus,
  registry,
  collapsed,
  onToggle,
  sort,
  onSortChange,
  onEdit,
  onAddProject,
}: StreamSectionProps): ReactElement {
  const setPositions = useSetPositions();
  const patchStream = usePatchStream();

  // The FULL manual order of the stream, unfiltered — a drop always sends the
  // whole stream to the server, also when a filter hides part of it.
  const manualOrder = useMemo(
    () => sortProjectsManual(registry.projects.filter((project) => project.streamId === stream.id)),
    [registry.projects, stream.id],
  );
  const order = useMemo(() => orderedIds(manualOrder), [manualOrder]);

  /** One reorder = optimistic cache update + POST with the FULL new order. */
  const applyOrder = (nextIds: readonly string[] | null) => {
    if (!nextIds || nextIds.length === 0) return;
    setPositions.mutate({ version: registry.version, streamId: stream.id, orderedIds: [...nextIds] });
  };

  const controls = useManualOrder({ enabled: sort === 'manual', busy: setPositions.isPending, order, onApply: applyOrder });

  const sectionId = `stream-${stream.id}`;
  // The drag list is the FULL (post-global, pre-status-filter) order — a drop
  // always sends the whole stream. The table is sorted from the post-status-filter
  // list, so the user sees their hidden statuses behind a "Сделано" pill click.
  const sorted =
    sort === 'manual'
      ? sortProjectsManual(visibleProjects)
      : sortProjects(visibleProjects, sort, undefined, undefined, undefined, statusRankLookup(registry.statuses));

  // Honest per-status counts: over the post-global, pre-status-filter set, so
  // the pill on a hidden status still shows the truth ("Сделано 4" is
  // meaningful even when «Сделано» is hidden — click to show them).
  const counts = useMemo(() => {
    const map = new Map<string, number>();
    for (const project of projects) {
      map.set(project.status, (map.get(project.status) ?? 0) + 1);
    }
    return map;
  }, [projects]);

  return (
    <section className="card card--flush stream-section" aria-label={`Поток: ${stream.name}`}>
      <header className="stream-section__header">
        <button
          type="button"
          className="stream-section__toggle"
          aria-expanded={!collapsed}
          aria-controls={sectionId}
          onClick={onToggle}
        >
          <ChevronDown
            size={16}
            aria-hidden="true"
            className={collapsed ? 'stream-section__chevron stream-section__chevron--closed' : 'stream-section__chevron'}
          />
          <span className="stream-section__name" title={stream.description || undefined}>
            {stream.name}
          </span>
          <span className="badge stream-section__count">{projects.length}</span>
        </button>
        {/* Per-status pills (owner request 2026-10-15; refined 2026-10-15:
            "right side after the count of project inside flow, without
            backgrounds, only when hovered to the flow name"). The pills
            are a SIBLING of the toggle — a <button> inside a <button>
            is invalid HTML. The hover region is the WHOLE LEFT SIDE of
            the header (toggle + pills) so the user's cursor moving from
            the name to a pill doesn't close the strip: the section's own
            :hover state keeps it open. Plain text: no fills, no borders;
            the count is appended after the label. The default-hide of
            «Сделано» (latched on first click) shows as GREEN TEXT, not
            a coloured pill. */}
        <span
          className="stream-section__pills"
          role="group"
          aria-label={`Фильтр по статусам в потоке «${stream.name}»`}
        >
          {statuses.map((status) => {
            const hidden = isHidden(status.id);
            const count = counts.get(status.id) ?? 0;
            return (
              <button
                key={status.id}
                type="button"
                className={
                  hidden && status.id === 'done'
                    ? 'status-pill status-pill--hidden-done'
                    : hidden
                      ? 'status-pill status-pill--hidden'
                      : 'status-pill'
                }
                aria-pressed={!hidden}
                title={hidden ? `Показать «${status.label}» в этом потоке` : `Скрыть «${status.label}» в этом потоке`}
                data-testid={`stream-status-${stream.id}-${status.id}`}
                onClick={() => onToggleStatus(status.id)}
              >
                <span className="status-pill__label">{status.label}</span>
                <span className="status-pill__count">{' '}{count}</span>
              </button>
            );
          })}
        </span>
        <div className="stream-section__tools">
          {/* «Ответственный» (owner request, 2026-10-14): a compact person
              select right in the flow header; a change PATCHes the stream
              immediately (the mutation returns the fresh document, the cache
              swaps it in). Self-contained — safe to move with the header. */}
          <StreamOwnerSelect stream={stream} registry={registry} busy={patchStream.isPending} />
          <label className="toolbar__label" htmlFor={`${sectionId}-sort`}>
            Сортировка
          </label>
          <select
            id={`${sectionId}-sort`}
            className="input input--compact"
            value={sort}
            onChange={(event) => onSortChange(event.target.value as StreamSortKey)}
          >
            {STREAM_SORT_KEYS.map((key) => (
              <option key={key} value={key}>
                {STREAM_SORT_LABELS[key]}
              </option>
            ))}
          </select>
          <button
            type="button"
            className="icon-btn icon-btn--sm"
            aria-label={`Добавить проект в поток «${stream.name}»`}
            title={`Добавить проект в поток «${stream.name}»`}
            data-testid={`stream-add-${stream.id}`}
            onClick={onAddProject}
          >
            <Plus size={14} aria-hidden="true" />
          </button>
        </div>
      </header>
      {!collapsed ? (
        <div id={sectionId}>
          <ProjectTable
            projects={sorted}
            registry={registry}
            onEdit={onEdit}
            emptyState={<FilteredEmptyState />}
            caption={`Проекты потока «${stream.name}»`}
            manualOrder={controls}
          />
        </div>
      ) : null}
    </section>
  );
}

/**
 * «Ответственный» of one stream (owner request, 2026-10-14): a compact select
 * over `registry.people` in the flow header. Empty option «—» = nobody
 * (`ownerPersonId: null`); a change PATCHes the stream AT ONCE — no save
 * button, the mutation answers the whole document and the cache swaps it in.
 * A person who is not active is still listed (the field may already point at
 * them) but marked.
 */
function StreamOwnerSelect({
  stream,
  registry,
  busy,
}: {
  stream: Stream;
  registry: RegistryDocument;
  busy: boolean;
}): ReactElement {
  const patchStream = usePatchStream();
  const owner = registry.people.find((person) => person.id === stream.ownerPersonId);
  const label = `Ответственный: ${stream.name}`;

  const setOwner = (personId: string) =>
    patchStream.mutate({
      id: stream.id,
      // FLAT dictionary body (arch §3.2); `null` CLEARS the field.
      body: { version: registry.version, ownerPersonId: personId === '' ? null : personId },
    });

  return (
    <>
      <label className="toolbar__label" htmlFor={`owner-${stream.id}`}>
        Ответственный
      </label>
      <select
        id={`owner-${stream.id}`}
        className="input input--compact stream-owner-select"
        aria-label={label}
        title={owner ? `Ответственный: ${owner.name}` : 'Ответственный не назначен'}
        value={stream.ownerPersonId ?? ''}
        disabled={busy}
        onChange={(event) => setOwner(event.target.value)}
      >
        <option value="">—</option>
        {registry.people.map((person: Person) => (
          <option key={person.id} value={person.id}>
            {person.active ? person.name : `${person.name} (неактивен)`}
          </option>
        ))}
      </select>
    </>
  );
}
